import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import setTextFixedFrameOperation from 'editor/src/store/design/operation/setTextFixedFrameOperation';
import getSelectedTextElementObject from 'editor/src/store/design/selector/getSelectedTextElementObject';
import { TextWrapping } from 'editor/src/store/design/types';
import isTextOverflowFramesAllowed from 'editor/src/store/editor/selector/isTextOverflowFramesAllowed';
import { canHaveFixedTextFrame } from 'editor/src/store/editorModules/warnings/utils/getTextOverflowWarning';
import applyFontStylesToSelectionOperation from 'editor/src/store/fonts/operation/applyFontStylesToSelectionOperation';
import getCurrentFontStylesProperty from 'editor/src/store/fonts/selector/getCurrentFontStylesProperty';
import { useDispatch, useSelector } from 'editor/src/store/hooks';
import { isEmbedded } from 'editor/src/store/watchers/useDisplayMode';

import Checkbox from '../Checkbox';
import IconInfo from '../Icon/IconInfo';
import SwitchControl from '../SwitchControl';
import WithTooltip from '../WithTooltip';

function TextFittingControl() {
  const wrapping = useSelector((state) => getCurrentFontStylesProperty(state, 'wrapping')) ?? TextWrapping.Wrap;
  const lockBoxSize = useSelector((state) => !!getSelectedTextElementObject(state)?.extra?.lockBoxSize);
  const fixedFrame = useSelector((state) => !!getSelectedTextElementObject(state)?.extra?.fixedFrame);
  // GAPI-61892: same conditions as the overflow check, so the frame cannot be turned on
  // where it would have no effect — but one that is on can always be turned off
  const fixedFrameApplies = useSelector((state) => {
    const element = getSelectedTextElementObject(state);
    return !!element && canHaveFixedTextFrame(element);
  });
  // GAPI-61892: offered only where allowed (see isTextOverflowFramesAllowed), or outside the embedded editor on a
  // text that already has one, so the author can always turn it back off. A buyer cannot remove a frame the
  // template set unless the control is allowed for them.
  const showFixedFrame = useSelector(isTextOverflowFramesAllowed) || (!isEmbedded && fixedFrame);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const onFitSwitch = useCallback(
    (on: boolean) => {
      dispatch(
        applyFontStylesToSelectionOperation({
          wrapping: on ? TextWrapping.Fit : undefined,
        }),
      );
    },
    [wrapping],
  );

  const onMultiLineSwitch = useCallback(
    (on: boolean) => {
      dispatch(
        applyFontStylesToSelectionOperation({
          wrapping: on ? TextWrapping.Fit : TextWrapping.FitOneLine,
        }),
      );
    },
    [wrapping],
  );

  const onFixedFrameChange = useCallback((on: boolean) => {
    dispatch(setTextFixedFrameOperation(on));
  }, []);

  const autoFitElt = useMemo(
    () => (
      <>
        {t('Autofit text')}
        <WithTooltip overlay={t('editor-autofit-tooltip')}>
          <IconInfo size="small" className="ml-1" />
        </WithTooltip>
      </>
    ),
    [],
  );

  return (
    <>
      <SwitchControl
        title={autoFitElt}
        on={wrapping === TextWrapping.Fit || wrapping === TextWrapping.FitOneLine}
        onSwitch={onFitSwitch}
        className="mb-2"
        disabled={lockBoxSize}
      />
      <Checkbox
        disabled={lockBoxSize || wrapping === TextWrapping.Wrap}
        on={wrapping !== TextWrapping.FitOneLine}
        label={t('editor-allow-multiline')}
        onChange={onMultiLineSwitch}
      />
      {showFixedFrame && (
        <Checkbox
          className="mt-2 cy-fixed-frame-checkbox"
          disabled={!fixedFrame && (!fixedFrameApplies || wrapping !== TextWrapping.Wrap)}
          on={fixedFrame}
          label={t('editor-text-fixed-frame')}
          onChange={onFixedFrameChange}
        />
      )}
    </>
  );
}

export default React.memo(TextFittingControl);
